/**
 * TUVI - FichaFFEPDF
 * Template visual para generar PDF de la Ficha FFE
 * Se renderiza oculto y se captura con html2canvas -> jsPDF
 */

import React, { forwardRef } from 'react';
import { BACKEND_URL } from '../../config';

// Observaciones fijas
const OBSERVACIONES = [
  'El presente documento corresponde a la ficha de especificaciones técnicas del proyecto y resume los materiales, referencias, cantidades y alcances aprobados durante la fase de diseño y presupuesto.',
  'Con la firma de aprobación de este documento, el cliente confirma que ha revisado y está de acuerdo con las especificaciones aquí descritas.',
  'Todo cambio posterior a la firma (modificación de referencias, cantidades, materiales o decisiones de diseño) podrá generar variación en tiempos de entrega y costo final. Estos adicionales, deberán enviarse por escrito y solo se ejecutarán después de su aprobación formal y ajuste económico correspondiente.',
  'Todos los adicionales solicitados por el contratante se deben dejar por escrito y firmado previo a la ejecución de obra.',
  'Una vez firmado este documento, se procederá con el pago del anticipo, necesario para asignar el ingeniero residente de obra y asegurar la disponibilidad de materiales y cronograma.',
  'Después de recibido el anticipo, el ingeniero residente entregará el cronograma oficial de ejecución.',
  'Las especificaciones de iluminación, traslados, nichos, buitrones y movimientos de redes estarán sujetas a validación en sitio durante la semana preoperativa, realizada por el Ingeniero Residente. Cualquier variación o condición técnica identificada será informada oportunamente al cliente antes de su ejecución.',
  'Este documento garantiza transparencia y trazabilidad en la ejecución del proyecto, buscando proteger los intereses del cliente y asegurar los estándares de calidad ofrecidos por Tuvi.'
];

// Orden fijo de espacios/categorías
const ORDEN_ESPACIOS = [
  'GENERALES',
  'COCINA',
  'HABITACIONES',
  'ESTUDIO',
  'BAÑOS',
  'ZONA DE ROPAS',
  'BALCÓN / TERRAZA',
  'FINALES'
];

// URL del proxy para Google Drive
const getProxyUrl = (url) => {
  if (!url) return null;
  if (url.includes('/upload/catalogo/') || url.includes('api.tuvisistema.com')) return url;
  return `${BACKEND_URL}/api/documentos/proxy-imagen.php?url=${encodeURIComponent(url)}`;
};

const FichaFFEPDF = forwardRef(({ cotizacion, actividades, planos = [], consideraciones = '', carrito = null }, ref) => {
  const {
    proyecto_nombre = '',
    apartamento = '',
    fecha = ''
  } = cotizacion || {};

  // Valor del presupuesto que firma el cliente: el de la cotización, ya con
  // descuento. Sale de la cotización que se está viendo, así que siempre es el
  // valor actualizado; si la cotización cambia, la ficha lo refleja.
  const fmtMoneda = (v) => '$ ' + Math.round(Number(v || 0)).toLocaleString('es-CO');
  const valorPresupuesto = Number(
    cotizacion?.valor_mostrar != null
      ? cotizacion.valor_mostrar
      : (Number(cotizacion?.valor_final_descuento) > 0
        ? cotizacion.valor_final_descuento
        : Math.max(0, Number(cotizacion?.valor_final || 0) - Number(cotizacion?.descuento || 0)))
  ) || 0;

  // Lechadas del carrito: solo las que tengan color o ubicación. Los bultos de
  // pega no van en la ficha que firma el cliente.
  const lechadas = (carrito?.lechadas || [])
    .map((l, i) => ({ n: i + 1, color: (l?.color || '').trim(), ubicacion: (l?.ubicacion || '').trim() }))
    .filter(l => l.color || l.ubicacion);

  // Agrupar actividades por espacio
  const actividadesPorEspacio = (actividades || []).reduce((acc, act) => {
    const raw = (act.espacio || '').trim().toUpperCase();
    const espacio = raw || 'GENERALES';
    if (!acc[espacio]) acc[espacio] = [];
    acc[espacio].push(act);
    return acc;
  }, {});

  // Fecha de hoy formateada
  const hoy = new Date();
  const fechaHoy = `${hoy.getDate()}/${hoy.getMonth() + 1}/${hoy.getFullYear()}`;

  return (
    <div ref={ref}>
      <div className="ffe-pdf-page">

        {/* ===== HEADER AREA ===== */}
        <div className="ffe-pdf-header-area">
          {/* Logo TUVI arriba derecha */}
          <img src="/logo_ffe.jpg" alt="TUVI" className="ffe-pdf-logo" crossOrigin="anonymous" />

          <div className="ffe-pdf-header-body">
            {/* Columna izquierda: verde oscuro con info */}
            <div className="ffe-pdf-header-left">
              <div className="ffe-pdf-icono-wrap">
                <img src="/icono_ffe.jpg" alt="TUVI" className="ffe-pdf-icono" crossOrigin="anonymous" />
              </div>
              <span className="ffe-pdf-pre-title">Especificaciones para</span>
              <span className="ffe-pdf-info-label">Proyecto</span>
              <span className="ffe-pdf-info-value">{proyecto_nombre}</span>
              <span className="ffe-pdf-info-label">Apartamento</span>
              <span className="ffe-pdf-info-value">{apartamento}</span>
              <span className="ffe-pdf-info-label">Última modificación</span>
              <span className="ffe-pdf-info-date">{fecha || fechaHoy}</span>
            </div>

            {/* Columna derecha: título + firma */}
            <div className="ffe-pdf-header-right">
              <h1 className="ffe-pdf-title">FICHA FFE</h1>
              <span className="ffe-pdf-subtitle">Documento de especificaciones: Muebles, accesorios y equipos</span>
              {/* El recuadro queda libre para que el cliente firme */}
              <div className="ffe-pdf-firma-espacio"></div>
              <div className="ffe-pdf-firma-pie">
                {valorPresupuesto > 0 && (
                  <span className="ffe-pdf-valor">
                    <span className="ffe-pdf-valor-label">Valor total del presupuesto</span>
                    <span className="ffe-pdf-valor-monto">{fmtMoneda(valorPresupuesto)}</span>
                  </span>
                )}
                <span className="ffe-pdf-firma-label">Firma aprobación:</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PLANOS ===== */}
        {planos.length > 0 && (
          <div className="ffe-pdf-planos-grid">
            {planos.map((plano, idx) => (
              <div key={idx} className={`ffe-pdf-plano${(plano.urls && plano.urls.filter(u => u).length > 1) ? ' ffe-pdf-plano-wide' : ''}`}>
                <div className="ffe-pdf-plano-header">{plano.titulo || `PLANO ${idx + 1}`}</div>
                <div className={`ffe-pdf-plano-body${(plano.urls && plano.urls.filter(u => u).length > 1) ? ' ffe-pdf-plano-body-grid' : ''}`}>
                  {(plano.urls && plano.urls.length > 0) ? (
                    plano.urls.filter(u => u).map((url, uIdx) => (
                      <img
                        key={uIdx}
                        src={getProxyUrl(url)}
                        alt={`${plano.titulo} ${uIdx + 1}`}
                        className="ffe-pdf-plano-img"
                        crossOrigin="anonymous"
                      />
                    ))
                  ) : plano.url ? (
                    <img
                      src={getProxyUrl(plano.url)}
                      alt={plano.titulo}
                      className="ffe-pdf-plano-img"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="ffe-pdf-plano-empty"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== CATEGORIAS / ESPACIOS ===== */}
        {[...ORDEN_ESPACIOS, ...Object.keys(actividadesPorEspacio).filter(e => !ORDEN_ESPACIOS.includes(e))].map(espacio => {
          const acts = actividadesPorEspacio[espacio] || [];
          if (acts.length === 0) return null;
          return (
            <div key={espacio} className="ffe-pdf-categoria">
              <div className="ffe-pdf-cat-header">{espacio}</div>
              <table className="ffe-pdf-cat-table">
                <thead>
                  <tr>
                    <th className="ffe-pdf-th-imagen">IMAGEN</th>
                    <th className="ffe-pdf-th-desc">DESCRIPCIÓN</th>
                    <th className="ffe-pdf-th-cant">CANTIDAD</th>
                    <th className="ffe-pdf-th-obs">OBSERVACIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {acts.map((act, idx) => (
                      <tr key={idx}>
                        <td className="ffe-pdf-td-imagen">
                          {act.url_imagen ? (
                            <img
                              src={getProxyUrl(act.url_imagen)}
                              alt={act.imagen_nombre || act.descripcion}
                              className="ffe-pdf-act-img"
                              crossOrigin="anonymous"
                            />
                          ) : (
                            <div className="ffe-pdf-act-img-empty"></div>
                          )}
                        </td>
                        <td className="ffe-pdf-td-desc">
                          <span className="ffe-pdf-act-desc">{act.descripcion_custom || act.descripcion}</span>
                        </td>
                        <td className="ffe-pdf-td-cant">
                          {(act.unidad === 'UND' || (act.capitulo && act.capitulo.toUpperCase().includes('CARPINTERIA')))
                            ? `${act.cantidad} ${act.unidad}`
                            : act.unidad}
                        </td>
                        <td className="ffe-pdf-td-obs">
                          <span className="ffe-pdf-act-obs">{act.observaciones || ''}</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {/* ===== LECHADAS ===== */}
        {lechadas.length > 0 && (
          <div className="ffe-pdf-pega">
            <div className="ffe-pdf-pega-title">Lechadas</div>
            <div className="ffe-pdf-pega-content">
              <table className="ffe-pdf-pega-table">
                <thead>
                  <tr>
                    <th className="ffe-pdf-pega-th-num">#</th>
                    <th>COLOR</th>
                    <th>UBICACIÓN</th>
                  </tr>
                </thead>
                <tbody>
                  {lechadas.map(l => (
                    <tr key={l.n}>
                      <td className="ffe-pdf-pega-td-num">Lechada {l.n}</td>
                      <td>{l.color || '—'}</td>
                      <td>{l.ubicacion || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== OBSERVACIONES ===== */}
        <div className="ffe-pdf-observaciones">
          <div className="ffe-pdf-obs-title">Observaciones</div>
          <div className="ffe-pdf-obs-content">
            {OBSERVACIONES.map((obs, idx) => (
              <p key={idx}>{idx + 1}. {obs}</p>
            ))}
          </div>
        </div>

        {/* ===== CONSIDERACIONES ESPECIALES ===== */}
        <div className="ffe-pdf-consideraciones">
          <div className="ffe-pdf-cons-title">Consideraciones especiales</div>
          <div className="ffe-pdf-cons-content">
            <p>{consideraciones || '*En este proyecto no se identifican consideraciones especiales.'}</p>
          </div>
        </div>

        {/* ===== FOOTER (imagen) ===== */}
        <div className="ffe-pdf-footer">
          <img src="/final_ficha_ffe.jpg" alt="TUVI" className="ffe-pdf-footer-img" crossOrigin="anonymous" />
        </div>

      </div>
    </div>
  );
});

FichaFFEPDF.displayName = 'FichaFFEPDF';

export default FichaFFEPDF;
